import { E_MasterListName } from "../../../../../enums/master-list-name.enum";
import { E_SocketMessageType } from "../../../../../enums/socket/socket-message-type.enum";
import { ChangesDetection } from "../../../../change-detection";
import { I_Element, I_MasterListItemResponse, I_MasterListResponse, I_OptionListDetails } from '../api/master-list.response';
import { I_OptionItemResponse } from "../api/option-list.response";
import { C_OptionItem } from './option-list.model';

export class C_MasterList extends ChangesDetection<T_MasterListChange> {
  id?: string;
  name?: string;
  description?: string;
  manualObservationName?: string;
  listType?: E_MasterListName;
  canAddElement?: boolean;
  optionListDetails?: C_OptionListDetails;
  options: Map<string, C_OptionItem> = new Map();
  private defaultOptionItem: C_OptionItem | undefined;
  constructor(json?: I_MasterListResponse) {
    super();
    if (!json) return;
    this.initializeFromJson(json);
  }

  getDefaultItem(): C_OptionItem | undefined {
    return this.defaultOptionItem;
  }

  toInterface(): I_MasterListResponse {
    return {
      id: this.id ?? '',
      listType: this.listType ?? E_MasterListName.UNDEFINED,
      name: this.name ?? '',
      description: this.description,
      manualObservationName: this.manualObservationName,
      canAddElement: !!this.canAddElement,
      optionListDetails: this.optionListDetails?.toInterface(),
      options: Array.from(this.options.values()),
    };
  }

  initializeFromJson(json: I_MasterListResponse) {
    if (json.id != null) this.id = json.id;
    if (json.manualObservationName != null) this.manualObservationName = json.manualObservationName;
    if (json.listType != null) this.listType = json.listType;
    if (json.name != null) this.name = json.name;
    if (json.description != null) this.description = json.description;
    if (json.canAddElement) this.canAddElement = !!json.canAddElement;
    if (json.optionListDetails != null) this.optionListDetails = new C_OptionListDetails(json.optionListDetails);
    if (json.options != null) this.setElementsArrayToMasterList(json.options);
    this.forceUpdateDetection({
      action: E_MasterListItemChangeAction.UpdatedList,
      data: json,
    });
  }

  initializeFromList(json: C_MasterList) {
    if (json.id != null) this.id = json.id;
    if (json.listType != null) this.listType = json.listType;
    if (json.name != null) this.name = json.name;
    if (json.description != null) this.description = json.description;
    if (json.canAddElement) this.canAddElement = !!json.canAddElement;
    if (json.optionListDetails != null) this.optionListDetails = json.optionListDetails;
    if (json.options != null) {
      this.setNewElementsArray(Array.from(json.options.values()));
    }
    this.forceUpdateDetection({
      action: E_MasterListItemChangeAction.UpdatedListFromList,
      data: json,
    });
  }

  manageItem(request: I_MasterListItemResponse, type: E_SocketMessageType) {
    switch (type) {
      case E_SocketMessageType.DELETE_MASTER_LIST_ITEM:
        this.deleteElementToMasterList(request);
        break;
      case E_SocketMessageType.ADD_MASTER_LIST_ITEM:
        this.setElementToMasterList(request);
        break;
      case E_SocketMessageType.UPDATE_MASTER_LIST_ITEM:
        this.updateElementToMasterList(request);
        break;
    }
  }
  deleteElementToMasterList(deletedElement: I_MasterListItemResponse) {
    if (deletedElement.deletedItem?.id == null) return;
    this.options.delete(deletedElement.deletedItem.id);
    this.forceUpdateDetection({
      action: E_MasterListItemChangeAction.DeleteListItem,
      id: deletedElement.id,
    });
  }
  setElementToMasterList(updatedElement: I_MasterListItemResponse) {
    if (updatedElement.newItem == null || !updatedElement.newItem.id) return;
    if (this.options.has(updatedElement.newItem.id)) this.options.get(updatedElement.newItem.id)?.initializeFromJson(updatedElement.newItem);
    else this.options.set(updatedElement.newItem.id, new C_OptionItem(updatedElement.newItem));
    if (updatedElement.newItem.isDefault) this.defaultOptionItem = this.options.get(updatedElement.id);
    this.forceUpdateDetection({
      action: E_MasterListItemChangeAction.SetListItem,
      data: updatedElement.newItem,
      id: updatedElement.id
    });
  }
  private updateElementToMasterList(updatedElement: I_MasterListItemResponse) {
    if (updatedElement.updatedItem == null || !updatedElement.updatedItem.id) return;
    if (!updatedElement.oldItem) return;
    if (this.options.has(updatedElement.oldItem.id)) this.options.get(updatedElement.oldItem.id)?.initializeFromJson(updatedElement.updatedItem);
    else this.options.set(updatedElement.oldItem.id, new C_OptionItem(updatedElement.updatedItem));
    if (updatedElement.updatedItem.isDefault) this.defaultOptionItem = this.options.get(updatedElement.id);
    this.forceUpdateDetection({
      action: E_MasterListItemChangeAction.UpdateListItem,
      data: updatedElement.updatedItem,
      id: updatedElement.id
    });
  }
  setNewElementsArray(items: I_OptionItemResponse[]) {
    this.options.clear();
    items.forEach(updatedElement => {
      if (!updatedElement.id) return;
      const newItem = new C_OptionItem(updatedElement);
      this.options.set(updatedElement.id, newItem);
      if (updatedElement.isDefault) this.defaultOptionItem = this.options.get(updatedElement.id);
    })
    this.forceUpdateDetection({
      action: E_MasterListItemChangeAction.SetListItemArray,
      data: items
    });
  }

  setElementsArrayToMasterList(items: I_OptionItemResponse[]) {
    items.forEach(updatedElement => {
      if (!updatedElement.id) return;
      const newItem = new C_OptionItem(updatedElement);
      if (this.options.has(updatedElement.id)) this.options.get(updatedElement.id)?.initializeFromJson(updatedElement);
      else this.options.set(updatedElement.id, newItem);
      if (updatedElement.isDefault) this.defaultOptionItem = this.options.get(updatedElement.id);
    });
    this.forceUpdateDetection({
      action: E_MasterListItemChangeAction.SetListItemArray,
      data: items
    });
  }
}

export class C_OptionListDetails {
  optionType?: C_Element;
  name?: C_Element;
  iconDefault?: C_Element;
  iconCategory?: C_Element;
  iconColor?: C_Element;
  color?: C_Element;
  bgColor?: C_Element;
  description?: C_Element;

  constructor(json?: I_OptionListDetails) {
    if (!json) return;
    this.initializeFromJson(json);
  }

  public initializeFromJson(json: I_OptionListDetails) {
    if (json.optionType != null) this.optionType = new C_Element(json.optionType);
    if (json.name != null) this.name = new C_Element(json.name);
    if (json.iconDefault != null) this.iconDefault = new C_Element(json.iconDefault);
    if (json.iconCategory != null) this.iconCategory = new C_Element(json.iconCategory);
    if (json.iconColor != null) this.iconColor = new C_Element(json.iconColor);
    if (json.color != null) this.color = new C_Element(json.color);
    if (json.bgColor != null) this.bgColor = new C_Element(json.bgColor);
    if (json.description != null) this.description = new C_Element(json.description);
  }

  toInterface(): I_OptionListDetails {
    return {
      optionType: this.optionType?.toInterface(),
      name: this.name?.toInterface(),
      iconDefault: this.iconDefault?.toInterface(),
      iconCategory: this.iconCategory?.toInterface(),
      iconColor: this.iconColor?.toInterface(),
      color: this.color?.toInterface(),
      bgColor: this.bgColor?.toInterface(),
      description: this.description?.toInterface(),
    }
  }
}

export class C_Element {
  title?: string;
  isRequired?: boolean;
  isList?: boolean;
  listName?: string;

  constructor(json?: I_Element) {
    if (!json) return;
    this.initializeFromJson(json);
  }

  public initializeFromJson(json: I_Element) {
    this.title = json.title;
    this.isRequired = json.isRequired;
    this.isList = json.isList;
    this.listName = json.listName;
  }

  toInterface(): I_Element {
    return {
      title: this.title,
      isRequired: this.isRequired,
      isList: this.isList,
      listName: this.listName,
    }
  }
}
export type T_MasterListChange =
  | { action: E_MasterListItemChangeAction.UpdatedList; data: I_MasterListResponse }
  | { action: E_MasterListItemChangeAction.UpdatedListFromList; data: C_MasterList }
  | { action: E_MasterListItemChangeAction.SetListItem; id: string; data: I_OptionItemResponse }
  | { action: E_MasterListItemChangeAction.UpdateListItem; id: string; data: I_OptionItemResponse }
  | { action: E_MasterListItemChangeAction.SetListItemArray; data: I_OptionItemResponse[] }
  | { action: E_MasterListItemChangeAction.DeleteListItem; id: string; };

export enum E_MasterListItemChangeAction {
  UpdatedList = "updated-list",
  UpdatedListFromList = "updated-list-from-list",
  SetListItemArray = "set-list-item",
  SetListItem = "set-list",
  UpdateListItem = "update-list",
  DeleteListItem = "delete-list-item",
}
